/**
 * Cart hooks - Quản lý state và mutations cho giỏ hàng
 *
 * File này chứa:
 * - Query hooks: Lấy data từ API (cart, cart mini, count items, etc.)
 * - Mutation hooks: Thao tác với cart (add, remove, update, etc.)
 *
 * Logic phức tạp được tách ra helper functions trong _helpers/ để code dễ đọc và maintain
 */

'use client'

import { toast } from '@/components/common/toast'
import { ToastAddToCartData } from '@/components/common/toast/toast-cart'
import { DeviceTokenHelper } from '@/lib/helpers/device-token.helper'
import {
  useIsBuy1Get1InGiftShopCampaignActive,
  useIsBuy1Get1ValueMatchCampaignActive,
} from '@/lib/utils/campaign/campaign-active'
import { CAMPAIGN_NAMES } from '@/lib/utils/campaign/campaign-names'
import { useCampaign, useCampaignActive } from '@/providers/campaign-provider'
import { __cCartService } from '@/services/client/__c.cart.service'
import {
  AddMultipleToCartParams,
  AddToCartParams,
  ChangeCartItemQuantityParams,
  ChangeWishlistItemQuantityParams,
  RemoveGiftItemParams,
  RemoveItemParams,
  UpdateCartItemParams,
  UpdateWishlistItemParams,
} from '@/types/cart'
import { AddToWishlistParams, RemoveWishlistItemParams } from '@/types/wishlist'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '../constants'
import { useIsLoggedIn } from '../user/use-user'
import {
  checkAndShowBuy1Get1ValueMatchToast,
  checkAndShowCampaignReminder,
  checkAndShowGiftEligibilityToast,
  showAddToCartSuccessToast,
} from './_helpers/add-to-cart-helpers'

// =================== QUERY HOOKS ===================

/**
 * Query hook để lấy thông tin giỏ hàng đầy đủ
 * @returns Cart data với tất cả items, pricing, vouchers, etc.
 */
export function useCartQuery() {
  const isLoggedIn = useIsLoggedIn()

  return useQuery({
    queryKey: [QUERY_KEYS.CART, isLoggedIn],
    queryFn: () => __cCartService.getCart({ isLoggedIn }),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  })
}

/**
 * Query hook để lấy thông tin giỏ hàng mini (cho cart dropdown)
 * @param enabled - Có enable query hay không (thường dùng để lazy load)
 */
export function useCartMiniQuery({ enabled }: { enabled: boolean }) {
  return useQuery({
    queryKey: [QUERY_KEYS.CART_MINI],
    queryFn: __cCartService.getCartMini,
    enabled,
  })
}

/**
 * Query hook để lấy số lượng items trong giỏ hàng
 * @returns Số lượng items (dùng cho badge trên cart icon)
 */
// Không dùng nữa, chuyển sang cart-mini cho đúng số lượng
// export function useCountCartItemQuery() {
//   return useQuery({
//     queryKey: [QUERY_KEYS.COUNT_CART_ITEM],
//     queryFn: __cCartService.countCartItem,
//   })
// }

/**
 * Query hook để lấy danh sách promotion products
 * @returns Danh sách sản phẩm khuyến mãi
 */
export function usePromotionProductsQuery() {
  return useQuery({
    queryKey: [QUERY_KEYS.PROMOTION_PRODUCTS],
    queryFn: () => __cCartService.getPromotionProducts(),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  })
}

// =================== MUTATION HOOKS ===================

/**
 * Mutation hook để thêm sản phẩm vào giỏ hàng
 *
 * Flow khi add to cart thành công:
 * 1. Hiển thị toast add to cart success
 * 2. Kiểm tra và hiển thị campaign reminder (nếu có)
 * 3. Invalidate cart queries để refetch data mới
 * 4. Kiểm tra và hiển thị gift eligibility toast (nếu campaign active)
 *
 * @returns Mutation object với mutateAsync function
 */
export function useAddToCart() {
  const queryClient = useQueryClient()
  const { data: cartMini, refetch: refetchCartMini } = useCartMiniQuery({
    enabled: true,
  })
  const campaignData = useCampaign(CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE)
  const isCampaignActive = useCampaignActive(CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE)
  const isBuy1Get1InGiftShopActive = useIsBuy1Get1InGiftShopCampaignActive()
  const isBuy1Get1ValueMatchActive = useIsBuy1Get1ValueMatchCampaignActive()

  return useMutation({
    mutationFn: async ({
      payload,
      toastOptions,
    }: {
      payload: Omit<AddToCartParams, 'device_token'>
      toastOptions?: ToastAddToCartData
    }) => {
      const fullPayload = {
        ...payload,
        device_token: DeviceTokenHelper.get(),
      }

      return __cCartService.addToCart(fullPayload, toastOptions)
    },
    onSuccess: async (toastOptions) => {
      try {
        // Lưu prevItems trước khi add để so sánh tìm item vừa add
        const prevItems = cartMini?.items ?? []

        // Step 1: Hiển thị toast add to cart success
        if (toastOptions) {
          await showAddToCartSuccessToast(toastOptions)
        }

        // Step 2: Kiểm tra và hiển thị campaign reminder
        // Capture cartMiniData để reuse trong gift eligibility toast
        const cartMiniData = await checkAndShowCampaignReminder({
          cartMini,
          refetchCartMini,
          campaignData,
          isCampaignActive,
          queryClient,
        })

        // Step 3: Invalidate cart queries để refetch data mới
        // Invalidate cart queries
        ;[
          QUERY_KEYS.CART,
          QUERY_KEYS.CART_MINI,
          QUERY_KEYS.COUNT_CART_ITEM,
          QUERY_KEYS.PROMOTION_PRODUCTS,
          QUERY_KEYS.VOUCHER_IN_CART,
          QUERY_KEYS.VOUCHER_ONLY_WALLET,
        ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))

        // Step 4: Kiểm tra và hiển thị gift eligibility toast
        // Chỉ gọi khi buy-1-get-1-in-gift-shop campaign đang active
        // Reuse cartMiniData từ campaign reminder để tránh fetch lại
        if (isBuy1Get1InGiftShopActive) {
          try {
            await checkAndShowGiftEligibilityToast({
              queryClient,
              imageUrl: toastOptions?.imageUrl,
              prevItems,
              cartMiniData, // Reuse data từ campaign reminder
            })
          } catch (error) {
            // Ignore CancelledError - có thể xảy ra khi component unmount hoặc mutation bị cancel
            if (
              error instanceof Error &&
              (error.name === 'CancelledError' ||
                error.message?.includes('CancelledError') ||
                error.message?.includes('cancelled'))
            ) {
              // Silently ignore cancellation errors - chỉ skip step này, không làm gián đoạn flow
              // Không return để Step 5 vẫn có thể chạy nếu cần
            } else {
              // Log other errors nhưng không throw để không làm gián đoạn flow
              console.error('Error in gift eligibility toast:', error)
            }
          }
        }

        // Step 5: Kiểm tra và hiển thị buy-1-get-1-value-match toast
        // Chỉ gọi khi buy-1-get-1-value-match campaign đang active
        // Refetch cart-mini để lấy max_eligible_price_for_gift mới nhất sau khi invalidate
        if (isBuy1Get1ValueMatchActive) {
          try {
            // Refetch cart-mini để lấy data mới nhất sau khi invalidate queries
            const refreshedCartMiniData = await queryClient.fetchQuery({
              queryKey: [QUERY_KEYS.CART_MINI],
              queryFn: () => __cCartService.getCartMini(),
              staleTime: 0, // Force fetch fresh data
            })

            await checkAndShowBuy1Get1ValueMatchToast({
              queryClient,
              cartMiniData: refreshedCartMiniData,
            })
          } catch (error) {
            // Ignore CancelledError - có thể xảy ra khi component unmount hoặc mutation bị cancel
            // Không làm gián đoạn flow chính
            if (
              error instanceof Error &&
              (error.name === 'CancelledError' ||
                error.message?.includes('CancelledError') ||
                error.message?.includes('cancelled'))
            ) {
              // Silently ignore cancellation errors - chỉ skip step này, không làm gián đoạn flow
              // Không cần return vì đây là step cuối cùng
            } else {
              // Log other errors nhưng không throw để không làm gián đoạn flow
              console.error('Error in buy-1-get-1-value-match toast:', error)
            }
          }
        }
      } catch (error) {
        // Catch any CancelledError or other errors that might occur in onSuccess
        // Ignore CancelledError - có thể xảy ra khi component unmount hoặc mutation bị cancel
        if (
          error instanceof Error &&
          (error.name === 'CancelledError' ||
            error.message?.includes('CancelledError') ||
            error.message?.includes('cancelled'))
        ) {
          // Silently ignore cancellation errors - không làm gián đoạn flow
          return
        }
        // Log other unexpected errors nhưng không throw để không làm gián đoạn flow
        console.error('Unexpected error in onSuccess:', error)
      }
    },
    onError: (error) => {
      if (error instanceof Error) {
        toast.message({
          title: 'Sorry!',
          message:
            error?.message || 'Something went wrong!Please try again later',
        })
      }
      console.error(error)
    },
  })
}

/**
 * Mutation hook để thêm nhiều sản phẩm vào giỏ hàng cùng lúc
 * Thường dùng cho wishlist -> cart hoặc bulk add
 */
export function useAddMultipleToCart() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, AddMultipleToCartParams, unknown>({
    mutationFn: (payload) =>
      __cCartService.addMultipleToCart({
        ...payload,
        device_token: DeviceTokenHelper.get(),
      }),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.WISHLIST,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

/**
 * Mutation hook để xóa một item khỏi giỏ hàng
 */
export function useRemoveCartItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, RemoveItemParams, unknown>({
    mutationFn: (payload: RemoveItemParams) =>
      __cCartService.removeItem(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.COUNT_CART_ITEM,
        QUERY_KEYS.CART_MINI,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
        QUERY_KEYS.PROMOTION_PRODUCTS,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

/**
 * Mutation hook để xóa một item khỏi cart mini (cart dropdown)
 * Chỉ invalidate các queries cần thiết cho cart mini
 */
export function useRemoveCartMiniItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, RemoveItemParams, unknown>({
    mutationFn: (payload: RemoveItemParams) =>
      __cCartService.removeItem(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.COUNT_CART_ITEM,
        QUERY_KEYS.CART_MINI,
        QUERY_KEYS.PROMOTION_PRODUCTS,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

/**
 * Mutation hook để xóa một gift item khỏi giỏ hàng
 */
export function useRemoveGiftItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, RemoveGiftItemParams, unknown>({
    mutationFn: (payload: RemoveGiftItemParams) =>
      __cCartService.removeGiftItem(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.COUNT_CART_ITEM,
        QUERY_KEYS.CART_MINI,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
        QUERY_KEYS.PROMOTION_PRODUCTS,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

/**
 * Mutation hook để xóa tất cả items khỏi giỏ hàng
 */
export function useRemoveAllCart() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, void, unknown>({
    mutationFn: () => __cCartService.removeAllCart(),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.CART_MINI,
        QUERY_KEYS.COUNT_CART_ITEM,
        QUERY_KEYS.WISHLIST,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

// =================== WISHLIST ===================

/**
 * Query hook để lấy danh sách wishlist
 */
export function useWishlistQuery() {
  return useQuery({
    queryKey: [QUERY_KEYS.WISHLIST],
    queryFn: () => __cCartService.getWishlist(),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  })
}

/**
 * Mutation hook để thêm sản phẩm vào wishlist
 */
export function useAddToWishlist() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, AddToWishlistParams, unknown>({
    mutationFn: (payload) => __cCartService.addToWishlist(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.WISHLIST,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

/**
 * Mutation hook để xóa một item khỏi wishlist
 */
export function useRemoveWishlistItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, RemoveWishlistItemParams, unknown>({
    mutationFn: (payload: RemoveWishlistItemParams) =>
      __cCartService.removeWishlistItem(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WISHLIST] })
    },
  })
}

/**
 * Mutation hook để update variant của một item trong wishlist
 */
export function useUpdateWishlistItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, UpdateWishlistItemParams, unknown>({
    mutationFn: (payload) => __cCartService.updateWishlistItem(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WISHLIST] })
    },
  })
}

/**
 * Mutation hook để update quantity của một item trong wishlist
 */
export function useUpdateWishlistItemQuantity() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, ChangeWishlistItemQuantityParams, unknown>(
    {
      mutationFn: (payload) =>
        __cCartService.updateWishlistItemQuantity(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WISHLIST] })
      },
    }
  )
}

// =================== UPDATE CART ITEMS ===================

/**
 * Mutation hook để update variant của một item trong cart
 * (ví dụ: đổi màu, size của sản phẩm)
 */
export function useUpdateCartItem() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, UpdateCartItemParams, unknown>({
    mutationFn: (payload) => __cCartService.updateCartItem(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.CART_MINI,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

/**
 * Mutation hook để update quantity của một item trong cart
 */
export function useUpdateCartItemQuantity() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, ChangeCartItemQuantityParams, unknown>({
    mutationFn: (payload) => __cCartService.updateCartItemQuantity(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.CART_MINI,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}

// =================== COOLCASH ===================

/**
 * Mutation hook để áp dụng CoolCash vào đơn hàng
 */
export function useApplyCoolCash() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, number, unknown>({
    mutationFn: (payload) => __cCartService.applyCoolCash(payload),
    onSuccess: () => {
      ;[
        QUERY_KEYS.CART,
        QUERY_KEYS.VOUCHER_IN_CART,
        QUERY_KEYS.VOUCHER_ONLY_WALLET,
      ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))
    },
  })
}
